import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { SITE_NAME, SITE_DESC } from "@/mocks/site";
import { FAQ_ITEMS } from "@/lib/content";

const PRINCIPLES = [
  { icon: "ri-eye-line", title: "不造谣，不传谣", desc: "故事仅基于公开信息与匿名投稿呈现，标注来源属性，不作确定性结论。" },
  { icon: "ri-shield-user-line", title: "隐私优先", desc: "涉及当事人的内容一律脱敏，当事人有异议可随时联系我们快速处理。" },
  { icon: "ri-mental-health-line", title: "拒绝网暴", desc: "倡导理性围观，不引导人肉搜索，不放大任何一方的恶意。" },
  { icon: "ri-flashlight-line", title: "天天更新", desc: "坚持每日滚动上新，让有趣的校园故事始终新鲜可读。" },
];

const TIMELINE = [
  { year: "萌芽期", text: "最初只是几个室友在熄灯后互相分享身边的小故事，聊着聊着发现大家都很需要这样一个轻松的树洞。" },
  { year: "成长期", text: "我们把分享搬到线上，按恋爱、表白、分手、纠纷等主题整理成栏目，故事越积越多，读者也越来越多。" },
  { year: "现在时", text: "今天的大学吃瓜坚持理性与趣味并重，希望成为你夜里随手点开、睡前心满意足的那款『电子瓜田』。" },
];

const REPUTATION = [
  { name: "熄灯前必刷", from: "某在校生", text: "内容真实不做作，最加分的是评论区总有人提醒别去打扰当事人。" },
  { name: "理性的温度", from: "高校老师", text: "难得见到一个娱乐向校园账号愿意反复强调隐私与边界，值得肯定。" },
  { name: "宿舍下饭神器", from: "大一新生", text: "恋爱栏目甜到蛀牙，纠纷复盘又能学到相处分寸，一晚上能刷很久。" },
];

export default function AboutPage() {
  const [openIdx, setOpenIdx] = useState(0);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "AboutPage", name: "关于我们", url: origin + "/about", description: `关于${SITE_NAME}的品牌介绍、定位、理念与原则` },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: origin,
      description: SITE_DESC,
      email: "hello@daxuechigua.example",
    },
  ];

  return (
    <>
      <Seo
        title="关于我们_大学吃瓜品牌与内容原则"
        description={`关于${SITE_NAME}：介绍我们的定位、发展理念、内容原则与用户口碑。${SITE_DESC}`}
        keywords="大学吃瓜,大学吃瓜关于我们,校园情感平台,内容原则"
        jsonLd={jsonLd}
      />
      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground-950 via-foreground-900 to-primary-800" />
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-primary-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-background-200/80">
            <Link to="/" className="flex items-center gap-1 hover:text-background-50"><i className="ri-home-line text-base" aria-hidden="true" />首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span aria-current="page" className="text-background-50">关于我们</span>
          </nav>
          <h1 className="mt-6 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">关于{SITE_NAME}</h1>
          <p className="mt-5 max-w-2xl text-base leading-7 text-background-200 md:text-lg">{SITE_DESC}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="mailto:hello@daxuechigua.example" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600">
              <i className="ri-mail-send-line" aria-hidden="true" /> 联系我们
            </a>
            <a href="#about-faq" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200/40 px-5 py-2.5 text-sm font-medium text-background-100 hover:bg-background-50/10">
              查看内容守则
            </a>
          </div>
        </div>
      </section>

      {/* positioning */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              <i className="ri-compass-3-line text-sm" aria-hidden="true" /> 我们是谁
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
              不只是吃瓜，
              <br />
              更是陪你读懂校园情感
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              {SITE_NAME}是一个面向在校大学生的校园情感内容平台。我们把大家愿意分享的恋爱故事、表白瞬间、分手与纠纷，整理成有人情味、有分寸感的文字，让它成为忙碌校园生活里一处轻松的落脚地。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              我们相信，每一段关系背后都是真实的人与真实的心情。所以比起夸张猎奇，我们更想把故事讲得真诚、克制，让读者笑一笑、叹口气，也能带走一点思考。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="rounded-xl border border-background-300/60 bg-background-100 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className={p.icon} aria-hidden="true" />
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* timeline */}
      <section className="bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
              <i className="ri-road-map-line text-sm" aria-hidden="true" /> 发展理念
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">从一个树洞，长成一片瓜田</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-600">我们的脚步并不快，但每一步都希望更真诚一点。</p>
          </div>
          <div className="relative mx-auto mt-12 max-w-4xl">
            <div className="absolute left-6 top-0 h-full w-px bg-background-300 md:left-1/2" aria-hidden="true" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <div key={t.year} className={`relative flex md:w-1/2 ${i % 2 === 0 ? "md:pr-12" : "md:ml-auto md:pl-12"}`}>
                  <span
                    className={`absolute left-6 top-1 z-10 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full ring-4 ring-background-50 ${
                      i % 2 === 0 ? "bg-accent-500" : "bg-secondary-500"
                    } md:left-full md:ml-0 ${i % 2 === 0 ? "md:translate-x-[-50%]" : "md:translate-x-[-50%]"}`}
                  />
                  <div className="ml-12 rounded-xl border border-background-300/60 bg-background-50 p-5 md:ml-0">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{t.year}</h3>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* reputation */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            <i className="ri-heart-3-line text-sm" aria-hidden="true" /> 读者口碑
          </span>
          <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">读者想对我们说的话</h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3 md:gap-5">
          {REPUTATION.map((r) => (
            <figure key={r.name} className="flex flex-col rounded-xl border border-background-300/60 bg-gradient-to-b from-background-100 to-background-50 p-6">
              <i className="ri-double-quotes-l text-3xl text-secondary-300" aria-hidden="true" />
              <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">{r.text}</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-bold text-primary-700">{r.name.slice(0, 1)}</span>
                <div className="text-sm">
                  <div className="font-bold text-foreground-950">{r.name}</div>
                  <div className="text-xs text-foreground-500">{r.from}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* faq / principles */}
      <section id="about-faq" className="bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                <i className="ri-question-line" aria-hidden="true" /> 内容守则 FAQ
              </span>
              <h2 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">内容原则与常见问题</h2>
              <p className="mt-3 text-sm leading-6 text-foreground-600">关于内容来源、隐私保护、投稿方式与更新节奏，这里都有答案。若仍有疑问，欢迎通过邮件与我们沟通。</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-4 py-2 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700">
                  <i className="ri-home-line" aria-hidden="true" /> 去首页逛逛
                </Link>
                <a href="mailto:hello@daxuechigua.example" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-4 py-2 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700">
                  <i className="ri-mail-line" aria-hidden="true" /> 邮件反馈
                </a>
              </div>
            </div>
            <div className="space-y-3 lg:col-span-3">
              {FAQ_ITEMS.map((item, i) => {
                const open = openIdx === i;
                return (
                  <div key={item.q} className={`overflow-hidden rounded-xl border transition-colors ${open ? "border-primary-300 bg-primary-50/50" : "border-background-300/60 bg-background-50"}`}>
                    <button type="button" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
                      <h3 className="flex items-center gap-3 font-heading text-[15px] font-bold text-foreground-950">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-black ${open ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900"}`}>{i + 1}</span>
                        {item.q}
                      </h3>
                      <i className={`ri-add-line text-xl text-foreground-600 transition-transform duration-300 ${open ? "rotate-45 text-primary-600" : ""}`} aria-hidden="true" />
                    </button>
                    {open && <p className="border-t border-primary-200/60 px-5 py-4 pl-14 text-sm leading-7 text-foreground-700">{item.a}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}